import React, { useEffect, useMemo, useState } from 'react';
import CustomModal from './CustomModal';
import useHideMobileNav from '../utils/useHideMobileNav';
import {
  buildPurchaseOrder,
  previewPurchaseOrder,
  sendPurchaseOrdersBatch,
} from '../services/purchaseOrderService';
import { vendorCanReceiveOrder } from '../utils/vendorContacts';

const toText = (value, fallback = '') => {
  if (value == null) return fallback;
  const next = String(value).trim();
  return next || fallback;
};

const getVendorContact = (vendor) => ({
  contactName: toText(vendor?.contact?.contactName || vendor?.contactName),
  contactEmail: toText(vendor?.contact?.contactEmail || vendor?.contactEmail),
  contactPhone: toText(vendor?.contact?.contactPhone || vendor?.contactPhone),
  secondaryContactName: toText(vendor?.contact?.secondaryContactName),
  secondaryContactEmail: toText(vendor?.contact?.secondaryContactEmail),
  secondaryContactPhone: toText(vendor?.contact?.secondaryContactPhone),
  generalEmail: toText(vendor?.contact?.generalEmail),
  generalPhone: toText(vendor?.contact?.generalPhone),
});

const statusLabelMap = {
  sent_by_email: 'Sent by email',
  pending_approval: 'Pending approval',
  sent_outside_countertop: 'Sent outside Countertop',
  not_sent_no_vendor_assigned: 'Not sent — no vendor assigned',
  not_sent_vendor_missing_email: 'Not sent — vendor has no contact email',
  error: 'Error',
  sending: 'Sending...',
};

const statusClassMap = {
  sent_by_email: 'vos-status-sent',
  pending_approval: 'vos-status-pending-approval',
  sent_outside_countertop: 'vos-status-sent-outside',
  not_sent_no_vendor_assigned: 'vos-status-blocked',
  not_sent_vendor_missing_email: 'vos-status-blocked',
  error: 'vos-status-error',
  sending: 'vos-status-sending',
};

const formatCountLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const getSendIssueSummary = (vendorRows = []) => {
  const summary = {
    unassignedItems: 0,
    missingEmailItems: 0,
    missingEmailVendors: 0,
    missingEmailVendorIds: [],
  };
  vendorRows.forEach((row) => {
    const itemCount = Array.isArray(row?.items) ? row.items.length : 0;
    if (row?.hasAssignedVendor === false || row?.vendorAssigned === false) {
      summary.unassignedItems += itemCount;
      return;
    }
    if (!row?.canSend) {
      summary.missingEmailItems += itemCount;
      summary.missingEmailVendors += 1;
      if (row?.vendorId) summary.missingEmailVendorIds.push(row.vendorId);
    }
  });
  // Dedupe vendor IDs so a vendor that appears in two rows of the
  // same selection isn't counted as two distinct missing-email vendors
  // — that would suppress the single-vendor deep-link incorrectly.
  summary.missingEmailVendorIds = Array.from(new Set(summary.missingEmailVendorIds));
  return summary;
};

// Build the URL we navigate to when the user clicks the
// "Add vendor contact information?" button. If we know the vendor's
// id we deep-link to its edit panel; otherwise we just open the
// vendor list and let the user pick.
const buildVendorEditorUrl = (vendorId) => (
  vendorId
    ? `/vendor-editor.html?editVendorId=${encodeURIComponent(vendorId)}`
    : '/vendor-editor.html'
);

const VendorOrderSendSheet = ({
  isOpen,
  title = 'Send to Vendors',
  subtitle = '',
  vendors = [],
  sourceMetadata = {},
  mode = 'send-only',
  onClose,
  onSkip,
  onAfterSend,
}) => {
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [statusByKey, setStatusByKey] = useState({});
  const [previewKey, setPreviewKey] = useState(null);
  const [showIssueModal, setShowIssueModal] = useState(false);

  const vendorRows = useMemo(() => (
    (vendors || []).map((vendor, index) => {
      const rowKey = `${vendor?.vendorId || vendor?.vendorName || 'vendor'}::${index}`;
      const contact = getVendorContact(vendor);
      const vendorAssigned = vendor?.vendorAssigned !== false;
      const canSend = vendor?.canSend ?? (vendorAssigned && vendorCanReceiveOrder(contact));
      return {
        ...vendor,
        rowKey,
        contact,
        vendorAssigned,
        canSend,
      };
    })
  ), [vendors]);

  const selectedVendorRows = useMemo(
    () => vendorRows.filter((row) => selectedKeys.has(row.rowKey)),
    [vendorRows, selectedKeys]
  );
  const selectedIssueSummary = useMemo(
    () => getSendIssueSummary(selectedVendorRows),
    [selectedVendorRows]
  );

  const previewVendor = useMemo(
    () => vendorRows.find((row) => row.rowKey === previewKey) || null,
    [previewKey, vendorRows]
  );

  useEffect(() => {
    if (!isOpen) return;
    const next = new Set(vendorRows.map((row) => row.rowKey));
    setSelectedKeys(next);
    setStatusByKey({});
    setPreviewKey(null);
    setShowIssueModal(false);
  }, [isOpen, vendorRows]);

  useHideMobileNav(!!isOpen);

  if (!isOpen) return null;

  const handleToggle = (rowKey) => {
    setSelectedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  const handleSelectAllValid = () => {
    setSelectedKeys(new Set(
      vendorRows
        .filter((row) => row?.sendReadinessCode !== 'no_vendor_assigned')
        .map((row) => row.rowKey)
    ));
  };

  const handleClearSelection = () => setSelectedKeys(new Set());

  const executeSendSelected = async ({ markOutsideForMissingEmail = false } = {}) => {
    if (!selectedVendorRows.length || sending) return;
    setSending(true);
    setStatusByKey({});

    const statusBuffer = {};
    selectedVendorRows.forEach((row) => {
      statusBuffer[row.rowKey] = 'sending';
    });
    setStatusByKey(statusBuffer);

    try {
      const summary = await sendPurchaseOrdersBatch(selectedVendorRows, {
        sourceMetadata,
        markOutsideForMissingEmail,
      });

      const nextStatuses = {};
      selectedVendorRows.forEach((row) => {
        const result = summary.results.find((entry) => (
          (entry.vendorId && row.vendorId && entry.vendorId === row.vendorId)
          || entry.vendorName === row.vendorName
        ));
        nextStatuses[row.rowKey] = result?.status || (
          row.sendReadinessCode === 'no_vendor_assigned'
            ? 'not_sent_no_vendor_assigned'
            : (row.sendReadinessCode === 'vendor_missing_email' ? 'not_sent_vendor_missing_email' : 'error')
        );
      });
      setStatusByKey(nextStatuses);

      if (typeof onAfterSend === 'function') onAfterSend(summary);
    } finally {
      setSending(false);
    }
  };

  const handleSendSelected = async () => {
    if (!selectedVendorRows.length || sending) return;
    const hasIssues = selectedIssueSummary.unassignedItems > 0 || selectedIssueSummary.missingEmailItems > 0;
    if (hasIssues) {
      setShowIssueModal(true);
      return;
    }
    await executeSendSelected({ markOutsideForMissingEmail: false });
  };

  const sendButtonLabel = mode === 'send-and-complete'
    ? `Send Selected (${selectedVendorRows.length}) & Finish`
    : `Send Selected (${selectedVendorRows.length})`;

  const previewPayload = previewVendor
    ? buildPurchaseOrder(previewVendor, sourceMetadata)
    : null;
  const previewEmail = previewPayload ? previewPurchaseOrder(previewPayload) : null;
  const issueLines = [
    selectedIssueSummary.unassignedItems > 0
      ? `${formatCountLabel(selectedIssueSummary.unassignedItems, 'item', 'items')} ${selectedIssueSummary.unassignedItems === 1 ? 'is' : 'are'} not assigned to a vendor.`
      : null,
    selectedIssueSummary.missingEmailItems > 0
      ? `${formatCountLabel(selectedIssueSummary.missingEmailItems, 'item', 'items')} ${selectedIssueSummary.missingEmailItems === 1 ? 'is' : 'are'} assigned to vendors with no contact email.`
      : null,
  ].filter(Boolean);
  return (
    <div className="vos-overlay" onClick={onClose}>
      <div className="vos-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="vos-header">
          <div>
            <h5>{title}</h5>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button type="button" className="vos-close" onClick={onClose} aria-label="Close send panel">&times;</button>
        </div>

        <div className="vos-toolbar">
          <button type="button" onClick={handleSelectAllValid}>Select all possible</button>
          <button type="button" onClick={handleClearSelection}>Clear</button>
        </div>

        <div className="vos-list">
          {vendorRows.length === 0 ? (
            <div className="vos-empty">No vendor order lines are available to send.</div>
          ) : (
            vendorRows.map((row) => {
              const status = statusByKey[row.rowKey] || '';
              const contactLine = [
                row.contact.contactName || 'No contact name',
                row.contact.contactEmail || 'No contact email',
                row.contact.contactPhone || 'No contact phone',
              ].join(' · ');

              return (
                <div className="vos-row" key={row.rowKey}>
                  <div className="vos-row-main">
                    <label className="vos-check">
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(row.rowKey)}
                        onChange={() => handleToggle(row.rowKey)}
                      />
                      <span>
                        <strong>{row.vendorName}</strong>
                        <small>{contactLine}</small>
                        {!row.canSend ? (
                          <small className="vos-warning">{row?.sendReadinessReason || 'Action needed before this vendor order can be sent.'}</small>
                        ) : null}
                      </span>
                    </label>
                    <div className="vos-row-actions">
                      <button type="button" onClick={() => setPreviewKey(row.rowKey)}>Preview</button>
                      {/* Inline shortcut: when a row is blocked specifically
                          because the vendor has no contact email, surface a
                          direct "Add vendor contact information?" button
                          right next to Preview that deep-links into the
                          vendor editor for this exact vendor. We key off
                          the explicit readiness code rather than just
                          `!canSend` so unrelated blockers don't show this
                          (mismatched) fix. */}
                      {row.sendReadinessCode === 'vendor_missing_email' ? (
                        <button
                          type="button"
                          className="vos-row-fix-btn"
                          onClick={() => {
                            window.location.href = buildVendorEditorUrl(row.vendorId);
                          }}
                        >
                          Add vendor contact information?
                        </button>
                      ) : null}
                      {status ? (
                        <span className={`vos-status ${statusClassMap[status] || ''}`}>
                          {statusLabelMap[status] || status}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="vos-footer">
          {mode === 'send-and-complete' ? (
            <button type="button" className="vos-btn-secondary" onClick={onSkip} disabled={sending}>
              Skip Sending
            </button>
          ) : (
            <button type="button" className="vos-btn-secondary" onClick={onClose} disabled={sending}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className="vos-btn-primary"
            onClick={handleSendSelected}
            disabled={sending || selectedVendorRows.length === 0}
          >
            {sending ? 'Sending...' : sendButtonLabel}
          </button>
        </div>
      </div>

      {previewVendor && previewPayload && previewEmail ? (
        <div className="vos-preview-overlay" onClick={() => setPreviewKey(null)}>
          <div className="vos-preview" onClick={(event) => event.stopPropagation()}>
            <div className="vos-preview-header">
              <strong>{previewVendor.vendorName} Preview</strong>
              <button type="button" onClick={() => setPreviewKey(null)}>&times;</button>
            </div>
            <div className="vos-preview-meta">
              <div><strong>Recipient:</strong> {previewPayload.contact.contactName || 'Not set'}</div>
              <div><strong>Email:</strong> {previewPayload.contact.contactEmail || 'Not set'}</div>
              {!previewVendor.canSend ? (
                <div className="vos-warning">{previewVendor?.sendReadinessReason || 'Action needed before this vendor order can be sent.'}</div>
              ) : null}
            </div>
            <div className="vos-preview-subject">
              <strong>Subject:</strong> {previewEmail.subject}
            </div>
            <pre>{previewEmail.text}</pre>
          </div>
        </div>
      ) : null}

      {showIssueModal ? (
        <CustomModal
          isOpen={showIssueModal}
          onClose={() => setShowIssueModal(false)}
          title="Some items cannot be sent to vendors"
          message="Some or all items in this order cannot be sent through Countertop."
          type="custom"
          messageAlign="left"
        >
          <div className="vos-issue-list">
            {issueLines.map((line) => (
              <div key={line}>• {line}</div>
            ))}
          </div>
          <div className="vos-issue-actions">
            <button
              type="button"
              className="vos-btn-primary"
              onClick={() => {
                setShowIssueModal(false);
                void executeSendSelected({ markOutsideForMissingEmail: true });
              }}
            >
              Send all possible items
            </button>
            <button
              type="button"
              className="vos-btn-secondary"
              onClick={() => {
                setShowIssueModal(false);
                window.location.href = '/items.html';
              }}
            >
              Go to Items and use AI to assign vendors
            </button>
            <button
              type="button"
              className="vos-btn-secondary"
              onClick={() => {
                setShowIssueModal(false);
                // If exactly one selected vendor is missing a contact email
                // we deep-link straight into its edit panel; otherwise we
                // open the vendor list so the user can choose.
                const ids = selectedIssueSummary.missingEmailVendorIds || [];
                const targetId = ids.length === 1 ? ids[0] : null;
                window.location.href = buildVendorEditorUrl(targetId);
              }}
            >
              Add vendor contact information?
            </button>
          </div>
        </CustomModal>
      ) : null}

      <style>{`
        /* Shared modal tone with Countertop's brown AI glyph. */
        .vos-ai-mark {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.2rem;
          height: 1.2rem;
          border-radius: 50%;
          background: #c8b5a0;
          color: #4a3428;
          font-size: 0.75rem;
          margin-right: 0.35rem;
        }
        .vos-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.45);
          z-index: 11000;
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }
        .vos-sheet {
          width: 100%;
          background: #fefcfa;
          border-radius: 16px 16px 0 0;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
        }
        .vos-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 0.9rem 1rem;
          border-bottom: 1px solid #e8ddd0;
        }
        .vos-header h5 {
          margin: 0;
          color: #3d2518;
        }
        .vos-header p {
          margin: 0.2rem 0 0;
          font-size: 0.82rem;
          color: #7a6e66;
        }
        .vos-close {
          border: none;
          background: none;
          font-size: 1.5rem;
          line-height: 1;
          color: #8b7b73;
        }
        .vos-toolbar {
          display: flex;
          justify-content: space-between;
          gap: 0.6rem;
          padding: 0.6rem 1rem;
          border-bottom: 1px solid #f2ebe5;
        }
        .vos-toolbar button {
          border: 1px solid #d6c7b8;
          background: #fff;
          color: #5a4a42;
          border-radius: 6px;
          padding: 0.35rem 0.6rem;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .vos-list {
          padding: 0.25rem 1rem 0.75rem;
          overflow-y: auto;
        }
        .vos-empty {
          color: #8b7b73;
          text-align: center;
          padding: 1rem 0;
          font-size: 0.82rem;
        }
        .vos-row {
          border: 1px solid #ebe1d8;
          border-radius: 10px;
          background: #fff;
          margin-bottom: 0.55rem;
          padding: 0.55rem;
        }
        .vos-row-main {
          display: flex;
          gap: 0.5rem;
          justify-content: space-between;
          align-items: flex-start;
        }
        .vos-check {
          display: flex;
          gap: 0.55rem;
          margin: 0;
          flex: 1;
        }
        .vos-check input {
          margin-top: 0.2rem;
        }
        .vos-check strong {
          display: block;
          font-size: 0.9rem;
          color: #3d2518;
        }
        .vos-check small {
          display: block;
          color: #7a6e66;
          font-size: 0.72rem;
        }
        .vos-row-actions {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.35rem;
        }
        .vos-row-actions button {
          border: 1px solid #d6c7b8;
          border-radius: 6px;
          background: #fff;
          color: #5a4a42;
          font-size: 0.72rem;
          font-weight: 600;
          padding: 0.25rem 0.5rem;
        }
        /* Highlight the inline "Add vendor contact information?" button so
           it reads as the recommended fix on a blocked vendor row. */
        .vos-row-actions .vos-row-fix-btn {
          background: #fff8e1;
          border-color: #e8c97a;
          color: #7a4f00;
          white-space: normal;
          text-align: center;
          line-height: 1.2;
          max-width: 11rem;
        }
        .vos-row-actions .vos-row-fix-btn:hover {
          background: #fceec2;
        }
        .vos-status {
          border-radius: 999px;
          font-size: 0.65rem;
          padding: 0.15rem 0.45rem;
          font-weight: 700;
        }
        .vos-status-sent {
          background: #e8f5e9;
          color: #2e7d32;
        }
        .vos-status-sent-outside {
          background: #f3e9dc;
          color: #6f4d2f;
        }
        .vos-status-pending-approval {
          background: #fff3e0;
          color: #b26a00;
        }
        .vos-status-blocked {
          background: #fff8e1;
          color: #a36400;
        }
        .vos-status-error {
          background: #ffebee;
          color: #c62828;
        }
        .vos-status-sending {
          background: #e3f2fd;
          color: #1565c0;
        }
        .vos-warning {
          color: #b26a00 !important;
          margin-top: 0.22rem;
          font-weight: 600;
        }
        .vos-footer {
          border-top: 1px solid #e8ddd0;
          padding: 0.8rem 1rem calc(0.8rem + env(safe-area-inset-bottom, 0px));
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.6rem;
          background: #fefcfa;
        }
        .vos-btn-secondary,
        .vos-btn-primary {
          border-radius: 10px;
          border: none;
          min-height: 44px;
          font-weight: 700;
          font-size: 0.83rem;
        }
        .vos-btn-secondary {
          background: #ede3da;
          color: #5a4a42;
        }
        .vos-btn-primary {
          background: #526b7c;
          color: #fff;
        }
        .vos-btn-primary:disabled,
        .vos-btn-secondary:disabled {
          opacity: 0.5;
        }
        .vos-preview-overlay {
          position: fixed;
          inset: 0;
          z-index: 11100;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          padding-top: calc(env(safe-area-inset-top, 0px) + 0.8rem);
          padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 0.8rem);
          padding-left: 0.8rem;
          padding-right: 0.8rem;
        }
        .vos-preview {
          width: min(680px, 100%);
          max-height: 88vh;
          overflow-y: auto;
          background: #fff;
          border-radius: 12px;
          border: 1px solid #e6dbd1;
          padding: 0.9rem;
        }
        .vos-preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: #3d2518;
        }
        .vos-preview-header button {
          border: none;
          background: none;
          font-size: 1.4rem;
          color: #8b7b73;
          line-height: 1;
        }
        .vos-preview-meta {
          margin-top: 0.45rem;
          font-size: 0.8rem;
          color: #5a4a42;
        }
        .vos-preview-subject {
          margin-top: 0.55rem;
          font-size: 0.78rem;
          color: #5a4a42;
        }
        .vos-preview pre {
          white-space: pre-wrap;
          font-size: 0.74rem;
          background: #faf6f2;
          border: 1px solid #eee3d8;
          border-radius: 8px;
          padding: 0.75rem;
          color: #4b3f37;
          margin: 0.55rem 0 0;
        }
        .vos-issue-list {
          margin-top: 0.7rem;
          font-size: 0.82rem;
          color: #5a4a42;
          display: grid;
          gap: 0.35rem;
        }
        .vos-issue-actions {
          margin-top: 0.9rem;
          display: grid;
          gap: 0.55rem;
        }
        .vos-issue-actions .vos-btn-secondary,
        .vos-issue-actions .vos-btn-primary {
          min-height: 40px;
        }
        @media (min-width: 769px) {
          .vos-overlay {
            align-items: center;
            padding: 1rem;
          }
          .vos-sheet {
            width: min(760px, 100%);
            border-radius: 14px;
            max-height: 88vh;
          }
        }
      `}</style>
    </div>
  );
};

export default VendorOrderSendSheet;
